import React, { useState } from 'react';
import { X, Mail, Archive, RotateCcw, Save, AlertCircle, Edit3 } from 'lucide-react';
import { format } from 'date-fns';
import { sr } from 'date-fns/locale';

const ListingDetailPanel = ({ listing, onClose, onArchive, onRestore, onSaveEdit, saving }) => {
  const [editMode, setEditMode] = useState(false);
  const [editedTitle, setEditedTitle] = useState(listing?.wishTitle || listing?.voucherName || '');
  const [editedDescription, setEditedDescription] = useState(listing?.description || '');
  const [editNote, setEditNote] = useState(listing?.editNote || '');

  const handleGmail = () => {
    const subject = encodeURIComponent(`ValueBook - Vaš oglas: ${listing?.wishTitle || listing?.voucherName}`);
    const body = encodeURIComponent(`Poštovani ${listing?.sellerName || 'korisniče'},\n\nObaveštavamo Vas u vezi sa Vašim oglasom na ValueBook platformi.\n\nNaslov oglasa: ${listing?.wishTitle || listing?.voucherName}\n\nS poštovanjem,\nValueBook Admin Tim`);
    window.open(`https://mail.google.com/mail/?view=cm&to=${listing?.sellerEmail}&su=${subject}&body=${body}`, '_blank');
  };

  const handleSave = () => {
    onSaveEdit(listing?.id, {
      wish_title: editedTitle,
      description: editedDescription,
      edit_note: editNote,
    });
    setEditMode(false);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-900">Detalji oglasa</h3>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-200 transition-colors">
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Meta */}
        <div className="bg-blue-50 rounded-lg p-3 text-sm space-y-1">
          <p><span className="font-medium text-gray-600">Prodavac:</span> <span className="text-gray-900">{listing?.sellerName || 'N/A'}</span></p>
          <p><span className="font-medium text-gray-600">Email:</span> <span className="text-gray-900">{listing?.sellerEmail || 'N/A'}</span></p>
          <p><span className="font-medium text-gray-600">Datum:</span> <span className="text-gray-900">{listing?.createdAt ? format(new Date(listing?.createdAt), 'dd.MM.yyyy HH:mm', { locale: sr }) : '-'}</span></p>
          <p><span className="font-medium text-gray-600">Tip:</span> <span className="text-gray-900">{listing?.wishType || 'N/A'}</span></p>
          {listing?.maxPrice && <p><span className="font-medium text-gray-600">Cena:</span> <span className="text-gray-900">{listing?.maxPrice} {listing?.currency}</span></p>}
        </div>

        {/* Edit note warning */}
        {listing?.editNote && (
          <div className="flex gap-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-yellow-800">Napomena o izmeni:</p>
              <p className="text-xs text-yellow-700 mt-0.5">{listing?.editNote}</p>
            </div>
          </div>
        )}

        {/* Title */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Naslov oglasa</label>
          {editMode ? (
            <input
              value={editedTitle}
              onChange={(e) => setEditedTitle(e?.target?.value)}
              className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          ) : (
            <p className="text-sm text-gray-900 font-medium">{listing?.wishTitle || listing?.voucherName || 'Bez naslova'}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Opis</label>
          {editMode ? (
            <textarea
              value={editedDescription}
              onChange={(e) => setEditedDescription(e?.target?.value)}
              rows={5}
              className="w-full border border-blue-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          ) : (
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{listing?.description || 'Nema opisa'}</p>
          )}
        </div>

        {/* Special conditions */}
        {listing?.specialConditions && (
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Posebni uslovi</label>
            <p className="text-sm text-gray-700">{listing?.specialConditions}</p>
          </div>
        )}

        {/* Edit note field */}
        {editMode && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <label className="block text-xs font-semibold text-yellow-800 mb-1">
              Napomena o izmeni (vidljiva korisniku)
            </label>
            <p className="text-xs text-yellow-700 mb-2">Izmene teksta oglasa zahtevaju saglasnost korisnika koji je postavio oglas.</p>
            <textarea
              value={editNote}
              onChange={(e) => setEditNote(e?.target?.value)}
              placeholder="Unesite razlog izmene..."
              rows={3}
              className="w-full border border-yellow-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none bg-white"
            />
          </div>
        )}
      </div>
      {/* Actions */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        {editMode ? (
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Čuvanje...' : 'Sačuvaj izmene'}
            </button>
            <button
              onClick={() => setEditMode(false)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Otkaži
            </button>
          </div>
        ) : (
          <button
            onClick={() => setEditMode(true)}
            className="w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 py-2.5 rounded-lg text-sm font-medium transition-colors"
          >
            <Edit3 className="w-4 h-4" />
            Uredi oglas
          </button>
        )}

        <button
          onClick={handleGmail}
          className="w-full flex items-center justify-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 py-2.5 rounded-lg text-sm font-medium transition-colors"
        >
          <Mail className="w-4 h-4 text-red-500" />
          Kontaktiraj prodavca (Gmail)
        </button>

        {listing?.isArchived ? (
          <button
            onClick={() => onRestore(listing?.id)}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <RotateCcw className="w-4 h-4" />
            Vrati oglas
          </button>
        ) : (
          <button
            onClick={() => onArchive(listing?.id)}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Archive className="w-4 h-4" />
            Arhiviraj oglas
          </button>
        )}
      </div>
    </div>
  );
};

export default ListingDetailPanel;
